/**
 * Example paths a path pattern matches, and a client-side check of whether a
 * pattern matches a path. Used to warn when two ways of ingesting files cover
 * the same files; the server's matcher stays the source of truth.
 */

/** Characters tried, in order, for a character class that excludes some. */
const NEGATED_CLASS_CANDIDATES = [..."xyz0123456789_"];

/**
 * A character the class `[members]` (or `[!members]` when `negated`) matches,
 * or undefined when none of the candidates fits or the class is not a valid
 * one, such as `[z-a]`.
 */
function classSample(negated: boolean, members: string): string | undefined {
  if (!negated) {
    return members[0];
  }
  let listed: RegExp;
  try {
    listed = new RegExp(`[${members}]`);
  } catch {
    return undefined;
  }
  return NEGATED_CLASS_CANDIDATES.find((char) => !listed.test(char));
}

/**
 * Fills one segment's wildcards (`*.mcap` → `example.mcap`), or gives null
 * when one of its character classes has no sample character.
 */
function fillSegment(segment: string): string | null {
  let fillable = true;
  const filled = segment
    .replace(/\*+/g, "example")
    .replace(/\?/g, "x")
    .replace(/\[([!^]?)([^\]]+)\]/g, (_, negation: string, members: string) => {
      const sample = classSample(negation !== "", members);
      fillable &&= sample !== undefined;
      return sample ?? "";
    })
    .replace(/\{([^,}]*)[^}]*\}/g, "$1");
  return fillable ? filled : null;
}

/**
 * Paths a pattern matches, at the top of a dataset and one folder down:
 * `**\/*.mcap` gives `example.mcap` and `dir/example.mcap`.
 */
export function samplePathsForPattern(pattern: string): string[] {
  let trimmed = pattern.trim().replace(/^\//, "");
  if (trimmed === "" || trimmed.startsWith("!")) {
    return [];
  }
  if (trimmed.endsWith("/")) {
    trimmed = `${trimmed}*`;
  }
  // A pattern without a folder matches a file name at any depth.
  const variants = trimmed.includes("/")
    ? [trimmed.replace(/\*\*\//g, ""), trimmed.replace(/\*\*\//g, "dir/")]
    : [trimmed, `dir/${trimmed}`];
  const paths = variants.flatMap((variant) => {
    const segments = variant
      .split("/")
      .map((segment) => (segment === "**" ? "dir" : fillSegment(segment)));
    return segments.includes(null) ? [] : [segments.join("/")];
  });
  return [...new Set(paths)];
}

/** {@link samplePathsForPattern} for every pattern, without repeats. */
export function samplePathsForPatterns(patterns: readonly string[]): string[] {
  return [...new Set(patterns.flatMap(samplePathsForPattern))];
}

function escapeRegExp(text: string): string {
  return text.replace(/[.+^${}()|\\]/g, "\\$&");
}

/** A `.gitignore`-style pattern as a regular expression over a whole path. */
function patternToRegExp(pattern: string): RegExp {
  let body = pattern.trim();
  const anchored = body.startsWith("/") || body.slice(0, -1).includes("/");
  body = body.replace(/^\//, "").replace(/\/$/, "");
  let source = "";
  for (let i = 0; i < body.length; i++) {
    const char = body[i];
    if (body.startsWith("**/", i)) {
      source += "(?:.*/)?";
      i += 2;
    } else if (body.startsWith("**", i)) {
      source += ".*";
      i += 1;
    } else if (char === "*") {
      source += "[^/]*";
    } else if (char === "?") {
      source += "[^/]";
    } else if (char === "[") {
      const end = body.indexOf("]", i + 1);
      if (end === -1) {
        source += "\\[";
      } else {
        source += `[${body.slice(i + 1, end).replace(/^!/, "^")}]`;
        i = end;
      }
    } else {
      source += escapeRegExp(char);
    }
  }
  // A match on a folder covers everything in it.
  return new RegExp(`^${anchored ? "" : "(?:.*/)?"}${source}(?:/.*)?$`);
}

/**
 * Whether a `.gitignore`-style path pattern, as triggers use, matches a path.
 * A `!` pattern never matches, and neither does one with a character class
 * that is not valid, such as `[z-a]`.
 */
export function pathPatternMatches(pattern: string, path: string): boolean {
  if (pattern.trim() === "" || pattern.startsWith("!")) {
    return false;
  }
  try {
    return patternToRegExp(pattern).test(path);
  } catch {
    return false;
  }
}
